import { FormEvent, useMemo, useState } from 'react'
import {
  calcularDesconto,
  calcularNegociacao,
  calcularParcelamento,
  calcularRetroativo,
  calcularTarifa,
  diffDaysUTC,
  format2,
  formatBRL,
  formatDateBR,
  formatInt,
  PARCELAMENTO_ENTRADAS,
  PARCELAMENTO_MINIMO,
  PARCELAMENTO_OPCOES,
  parseBRNumber,
  parseDateBR,
  todayBRString,
  todayUTC,
  type DiscountResult,
  type NegociacaoResult,
  type RetroativoResult,
  type TarifaCategoria,
  type TarifaResult,
} from './calculators'
import {
  ENTRADA_PADRAO_FIQUE_EM_DIA, PARCELA_MINIMA_FIQUE_EM_DIA,
  type ParcelamentoCampanha,
} from '../../lib/parcelamentoCampanha'

export type ToolKey = 'desconto' | 'retro' | 'tarifa' | 'parcel' | 'negocia'

export const OPERATIONAL_TOOLS: Array<{ key: ToolKey; label: string; description: string; icon: string }> = [
  { key: 'desconto', label: 'Simulador de Desconto', description: 'Calcula elegibilidade e cobrança sugerida por média anual.', icon: '▣' },
  { key: 'retro', label: 'Cálculo Retroativo', description: 'Calcula consumo proporcional e leitura corrigida por período.', icon: '↺' },
  { key: 'tarifa', label: 'Valor por m³', description: 'Calcula água, esgoto e tarifa fixa por faixa de consumo.', icon: 'm³' },
  { key: 'parcel', label: 'Simulador de Parcelamento', description: 'Simula entrada e parcelas respeitando o mínimo operacional.', icon: '◴' },
  { key: 'negocia', label: 'Negociação de Dívidas', description: 'Aplica faixa de atraso, descontos e limite de parcelamento.', icon: '$' },
]

function Status({ kind = 'info', children }: { kind?: 'info' | 'ok' | 'warn' | 'error'; children: React.ReactNode }) {
  return <div className={`tool-status ${kind}`} role="status">{children}</div>
}

function Metric({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <article className="tool-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </article>
  )
}

function ToolHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="tool-heading">
      <div>
        <p className="eyebrow">FERRAMENTA OPERACIONAL</p>
        <h2>{title}</h2>
        <p className="muted">{description}</p>
      </div>
      <span className="status-chip">Cálculo local</span>
    </div>
  )
}

function DescontoTool() {
  const [referencia, setReferencia] = useState('')
  const [consumo, setConsumo] = useState('')
  const [atual, setAtual] = useState('')
  const [historica, setHistorica] = useState('')
  const [primeiro, setPrimeiro] = useState(true)
  const [result, setResult] = useState<DiscountResult | null>(null)

  function submit(event: FormEvent) {
    event.preventDefault()
    setResult(calcularDesconto({
      consumo: parseBRNumber(consumo),
      leituraAtual: parseBRNumber(atual),
      leitura12m: parseBRNumber(historica),
      primeiroDesconto: primeiro,
    }))
  }

  function clear() {
    setReferencia('')
    setConsumo('')
    setAtual('')
    setHistorica('')
    setPrimeiro(true)
    setResult(null)
  }

  return (
    <section className="operational-tool">
      <ToolHeader title="Simulador de Desconto" description="Regras operacionais preservadas da Suite v7.5, com elegibilidade a partir de 50 m³." />
      <div className="tool-layout">
        <form className="tool-card tool-form" onSubmit={submit}>
          <label>Mês de referência (MM/AAAA)<input value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="07/2025" /></label>
          <label>Consumo medido (m³)<input inputMode="decimal" value={consumo} onChange={(e) => setConsumo(e.target.value)} placeholder="Ex.: 120" /></label>
          <label>Leitura atual<input inputMode="decimal" value={atual} onChange={(e) => setAtual(e.target.value)} placeholder="Ex.: 12500" /></label>
          <label>Leitura de 12 meses atrás<input inputMode="decimal" value={historica} onChange={(e) => setHistorica(e.target.value)} placeholder="Ex.: 11760" /></label>
          <label>Primeiro desconto do ano?
            <select value={primeiro ? 's' : 'n'} onChange={(e) => setPrimeiro(e.target.value === 's')}>
              <option value="s">Sim</option>
              <option value="n">Não</option>
            </select>
          </label>
          <div className="tool-rule"><span>Regra operacional</span><strong>Elegibilidade a partir de 50 m³</strong></div>
          <div className="tool-actions span-2"><button type="button" className="secondary-button" onClick={clear}>Limpar</button><button className="primary-button compact" type="submit">Calcular</button></div>
        </form>

        <section className="tool-card tool-result">
          <h3>Resultado</h3>
          {!result && <Status>Preencha os campos e calcule.</Status>}
          {result && 'error' in result && <Status kind="error">{result.error}</Status>}
          {result && !('error' in result) && !result.eligible && (
            <>
              <Status kind="warn">Sem desconto: consumo abaixo do mínimo operacional.</Status>
              <div className="tool-metrics"><Metric label="Média anual" value={`${format2(result.mediaAnual)} m³`} detail="Cálculo informativo" /><Metric label="Limite de comparação" value={`${format2(result.limite200)} m³`} detail="Regra 200%" /></div>
              <div className="result-copy">Referência {referencia || 'não informada'}: consumo de {format2(parseBRNumber(consumo))} m³ não alcança a faixa de elegibilidade.</div>
            </>
          )}
          {result && !('error' in result) && result.eligible && !result.desconto && (
            <>
              <Status kind="ok">Sem desconto: consumo dentro do intervalo permitido.</Status>
              <div className="tool-metrics three"><Metric label="Média anual" value={`${format2(result.mediaAnual)} m³`} /><Metric label="Limite 200%" value={`${format2(result.limite200)} m³`} /><Metric label="Consumo medido" value={`${format2(parseBRNumber(consumo))} m³`} /></div>
              <div className="result-copy">Referência {referencia || 'não informada'}: consumo enquadrado sem concessão de desconto.</div>
            </>
          )}
          {result && !('error' in result) && result.eligible && result.desconto && (
            <>
              <Status kind="ok">Desconto aplicável: cobrança sugerida calculada.</Status>
              <div className="tool-metrics three"><Metric label="Cobrança sugerida" value={`${formatInt(result.cobrar ?? Number.NaN)} m³`} detail="Resultado arredondado" /><Metric label="Média anual" value={`${format2(result.mediaAnual)} m³`} /><Metric label="Fator aplicado" value={format2(result.fator ?? Number.NaN)} detail={primeiro ? 'Primeiro desconto do ano' : 'Recorrência no mesmo ano'} /></div>
              <div className="result-copy">REFERÊNCIA {referencia || 'N/I'} | COBRAR: {formatInt(result.cobrar ?? Number.NaN)} m³ | MÉDIA ANUAL: {format2(result.mediaAnual)} m³ | FATOR: {format2(result.fator ?? Number.NaN)}</div>
            </>
          )}
        </section>
      </div>
    </section>
  )
}

function RetroativoTool() {
  const [dataOk, setDataOk] = useState('')
  const [dataCliente, setDataCliente] = useState('')
  const [dataErro, setDataErro] = useState('')
  const [atual, setAtual] = useState('')
  const [correta, setCorreta] = useState('')
  const [result, setResult] = useState<RetroativoResult | null>(null)

  function submit(event: FormEvent) {
    event.preventDefault()
    setResult(calcularRetroativo({
      dataLeituraCorreta: parseDateBR(dataOk),
      dataCliente: parseDateBR(dataCliente),
      dataErro: parseDateBR(dataErro),
      leituraAtual: parseBRNumber(atual),
      leituraCorreta: parseBRNumber(correta),
    }))
  }

  function clear() {
    setDataOk(''); setDataCliente(''); setDataErro(''); setAtual(''); setCorreta(''); setResult(null)
  }

  const parsedCliente = parseDateBR(dataCliente)
  const withinFiveDays = parsedCliente ? (() => { const days = diffDaysUTC(parsedCliente, todayUTC()); return days >= 0 && days <= 5 })() : false

  return (
    <section className="operational-tool">
      <ToolHeader title="Cálculo Retroativo" description="Valida a sequência das datas e calcula o consumo proporcional até a leitura incorreta." />
      <div className="tool-layout">
        <form className="tool-card tool-form" onSubmit={submit}>
          <label>1. Data da última leitura correta<input value={dataOk} onChange={(e) => setDataOk(e.target.value)} placeholder="01/07/2025" /></label>
          <label>2. Data informada pelo cliente<input value={dataCliente} onChange={(e) => setDataCliente(e.target.value)} placeholder="10/07/2025" /></label>
          <label>3. Data da leitura errada<input value={dataErro} onChange={(e) => setDataErro(e.target.value)} placeholder="08/07/2025" /></label>
          <label>4. Leitura atual<input inputMode="decimal" value={atual} onChange={(e) => setAtual(e.target.value)} placeholder="12500" /></label>
          <label>5. Última leitura correta<input inputMode="decimal" value={correta} onChange={(e) => setCorreta(e.target.value)} placeholder="12000" /></label>
          <div className="tool-actions span-2"><button type="button" className="secondary-button" onClick={clear}>Limpar</button><button className="primary-button compact" type="submit">Calcular</button></div>
        </form>
        <section className="tool-card tool-result">
          <h3>Resultado</h3>
          {!result && <Status>Preencha os campos e calcule.</Status>}
          {result && 'error' in result && <Status kind="error">{result.error}</Status>}
          {result && !('error' in result) && (
            <>
              <Status kind={withinFiveDays ? 'warn' : 'ok'}>{withinFiveDays ? 'Data do cliente dentro de 5 dias: seguir procedimento de erro comum.' : 'Cálculo retroativo concluído.'}</Status>
              <div className="tool-metrics three"><Metric label="Dias entre leituras" value={formatInt(result.diasTotal)} /><Metric label="Dias até o erro" value={formatInt(result.diasErro)} /><Metric label="Consumo por dia" value={`${format2(result.m3Dia)} m³`} /></div>
              <div className="tool-metrics"><Metric label="m³ a cobrar" value={`${format2(result.m3Cobrar)} m³`} detail={`Consumo total: ${format2(result.consumoTotal)} m³`} /><Metric label="Leitura corrigida" value={`${formatInt(result.leituraCorrigida)} m³`} detail={`Base em ${formatDateBR(parseDateBR(dataErro))}`} /></div>
              <div className="result-copy">RETROATIVO | {result.diasErro} dia(s) até o erro | {format2(result.m3Cobrar)} m³ a cobrar | leitura corrigida {formatInt(result.leituraCorrigida)}</div>
            </>
          )}
        </section>
      </div>
    </section>
  )
}

function TarifaTool() {
  const [categoria, setCategoria] = useState<TarifaCategoria>('residencial')
  const [consumo, setConsumo] = useState('')
  const [result, setResult] = useState<TarifaResult | null>(null)

  function submit(event: FormEvent) {
    event.preventDefault()
    setResult(calcularTarifa(categoria, parseBRNumber(consumo)))
  }

  return (
    <section className="operational-tool">
      <ToolHeader title="Valor por m³" description="Composição tarifária 2026 por categoria e faixa de consumo, incluindo água, esgoto e tarifa fixa." />
      <div className="tool-layout">
        <form className="tool-card tool-form compact" onSubmit={submit}>
          <label>Categoria<select value={categoria} onChange={(e) => setCategoria(e.target.value as TarifaCategoria)}><option value="social">Social</option><option value="residencial">Residencial</option><option value="comercial">Comercial</option></select></label>
          <label>Consumo (m³)<input inputMode="decimal" value={consumo} onChange={(e) => setConsumo(e.target.value)} placeholder="Ex.: 32" /></label>
          <div className="tool-actions span-2"><button type="button" className="secondary-button" onClick={() => { setCategoria('residencial'); setConsumo(''); setResult(null) }}>Limpar</button><button className="primary-button compact" type="submit">Calcular</button></div>
        </form>
        <section className="tool-card tool-result">
          <h3>Resultado</h3>
          {!result && <Status>Selecione categoria, informe o consumo e calcule.</Status>}
          {result && 'error' in result && <Status kind="error">{result.error}</Status>}
          {result && !('error' in result) && (
            <>
              <Status kind={result.socialFallback ? 'warn' : 'ok'}>{result.socialFallback ? 'Tarifa social acima de 20 m³: mantido o fallback informado para 2026.' : 'Cálculo tarifário concluído.'}</Status>
              <div className="tool-metrics three"><Metric label="Água" value={formatBRL(result.agua)} /><Metric label="Esgoto" value={formatBRL(result.esgoto)} /><Metric label="Tarifa fixa" value={formatBRL(result.fixa)} /></div>
              <div className="tool-table-wrap"><table className="tool-table"><thead><tr><th>Faixa</th><th>Limite</th><th>Consumo</th><th>Água</th><th>Esgoto</th></tr></thead><tbody>{result.detalhes.map((item) => <tr key={item.faixa}><td>{item.faixa}</td><td>{Number.isFinite(item.limite) ? `${formatInt(item.limite)} m³` : '∞'}</td><td>{format2(item.usado)} m³</td><td>{formatBRL(item.valorAgua)}</td><td>{formatBRL(item.valorEsgoto)}</td></tr>)}</tbody></table></div>
              <div className="result-copy">{categoria.charAt(0).toUpperCase() + categoria.slice(1)} | {format2(result.consumo)} m³ | Água {formatBRL(result.agua)} | Esgoto {formatBRL(result.esgoto)} | Total {formatBRL(result.total)}</div>
              <Metric label="Total a pagar" value={formatBRL(result.total)} />
            </>
          )}
        </section>
      </div>
    </section>
  )
}

function ParcelamentoTool({ campanha, onVoltarCampanha }: {
  campanha?: ParcelamentoCampanha | null
  onVoltarCampanha?: () => void
}) {
  const entradaInicial = campanha ? ENTRADA_PADRAO_FIQUE_EM_DIA : null
  const [valorText, setValorText] = useState(campanha ? formatBRL(campanha.valorFinal) : '')
  const [entrada, setEntrada] = useState<number | null>(entradaInicial)
  const minimoParcela = campanha ? PARCELA_MINIMA_FIQUE_EM_DIA : PARCELAMENTO_MINIMO
  const maxParcelas = campanha ? campanha.maxParcelas : Number.POSITIVE_INFINITY
  const [parcelas, setParcelas] = useState<number | null>(() => {
    if (!campanha) return null
    return PARCELAMENTO_OPCOES.find((count) => {
      if (count > campanha.maxParcelas) return false
      const calc = calcularParcelamento(campanha.valorFinal, ENTRADA_PADRAO_FIQUE_EM_DIA, count, PARCELA_MINIMA_FIQUE_EM_DIA)
      return !('error' in calc) && calc.valido
    }) ?? null
  })
  const [copyMessage, setCopyMessage] = useState('')
  const valor = parseBRNumber(valorText, { digitsAsCents: true })

  const options = useMemo(() => PARCELAMENTO_OPCOES.filter((count) => count <= maxParcelas).map((count) => {
    if (!Number.isFinite(valor) || valor <= 0 || entrada == null) return { count, valid: false, amount: Number.NaN }
    const calc = calcularParcelamento(valor, entrada, count, minimoParcela)
    return 'error' in calc ? { count, valid: false, amount: Number.NaN } : { count, valid: calc.valido, amount: calc.valorParcela }
  }), [valor, entrada, minimoParcela, maxParcelas])

  const selected = entrada != null && parcelas != null && parcelas <= maxParcelas
    ? calcularParcelamento(valor, entrada, parcelas, minimoParcela) : null
  const validSelected = selected && !('error' in selected) && selected.valido ? selected : null

  function chooseEntry(value: number) {
    setEntrada(value)
    const first = PARCELAMENTO_OPCOES.find((count) => {
      if (count > maxParcelas) return false
      const calc = calcularParcelamento(valor, value, count, minimoParcela)
      return !('error' in calc) && calc.valido
    })
    setParcelas(first ?? null)
    setCopyMessage('')
  }

  async function copySummary() {
    if (!validSelected) return
    const origin = campanha ? `FIQUE EM DIA 2026 - ETAPA ${campanha.etapa} | ` : ''
    const text = `${origin}VALOR TOTAL: ${formatBRL(validSelected.valor)} | ENTRADA ${Math.round(validSelected.entradaPerc * 100)}%: ${formatBRL(validSelected.entrada)} | ${validSelected.parcelas}X DE ${formatBRL(validSelected.valorParcela)}`
    try { await navigator.clipboard.writeText(text); setCopyMessage('Resumo copiado.') } catch { setCopyMessage('Não foi possível copiar automaticamente.') }
  }

  function clear() { setValorText(''); setEntrada(null); setParcelas(null); setCopyMessage('') }

  return (
    <section className="operational-tool">
      <ToolHeader title="Simulador de Parcelamento"
        description={campanha
          ? `Fique em Dia 2026: Etapa ${campanha.etapa}. Entrada de 10%, parcelas a partir de R$ 50,00 e até ${campanha.maxParcelas} vezes, conforme a base da campanha.`
          : 'Entradas de 30%, 20% ou 10%, com bloqueio automático de parcelas abaixo do mínimo de R$ 20,00.'} />
      {campanha && (
        <div className="tool-status info" role="status">
          <strong>Valor recebido da Etapa {campanha.etapa} do Fique em Dia 2026.</strong>
          {' '}Este simulador usa o valor final da base e não recalcula o desconto da campanha.
          A entrada de 10% fica pré-selecionada.
          {onVoltarCampanha && (
            <div style={{ marginTop: 12 }}>
              <button type="button" className="secondary-button compact" onClick={onVoltarCampanha}>
                ← Voltar às etapas da campanha
              </button>
            </div>
          )}
        </div>
      )}
      <div className="tool-layout">
        <section className="tool-card stack">
          <label className="tool-label">{campanha ? 'Valor final da negociação (R$)' : 'Valor total (R$)'}
            <input inputMode="numeric" value={valorText} readOnly={Boolean(campanha)}
              onChange={(e) => { if (campanha) return; setValorText(e.target.value); setEntrada(null); setParcelas(null) }}
              onBlur={() => { if (!campanha && Number.isFinite(valor) && valor > 0) setValorText(formatBRL(valor)) }}
              placeholder="Ex.: 536220 → R$ 5.362,20" />
          </label>
          <div><span className="tool-section-label">{campanha ? 'Entrada da campanha (10%)' : 'Escolha a entrada'}</span>
            <div className="entry-grid">
              {(campanha ? [ENTRADA_PADRAO_FIQUE_EM_DIA] : PARCELAMENTO_ENTRADAS).map((perc) => (
                <button key={perc} type="button" className={`entry-card ${entrada === perc ? 'active' : ''}`}
                  disabled={Boolean(campanha) || !Number.isFinite(valor) || valor <= 0} onClick={() => chooseEntry(perc)}>
                  <strong>{Math.round(perc * 100)}%</strong>
                  <span>{Number.isFinite(valor) && valor > 0 ? `Entrada ${formatBRL(valor * perc)}` : 'Aguardando valor'}</span>
                </button>
              ))}
            </div>
          </div>
          <div><span className="tool-section-label">Quantidade de parcelas</span>
            <div className="parcel-grid">
              {options.map((option) => (
                <button key={option.count} type="button" className={`parcel-option ${parcelas === option.count ? 'active' : ''}`}
                  disabled={!option.valid} onClick={() => setParcelas(option.count)}>
                  <strong>{option.count}x</strong>
                  <span>{Number.isFinite(option.amount) ? option.valid ? `${formatBRL(option.amount)} por parcela` : `Abaixo de ${formatBRL(minimoParcela)}` : 'Selecione entrada'}</span>
                </button>
              ))}
            </div>
            {campanha && !options.length && <p className="muted">A base da campanha não permite nenhum dos prazos disponíveis neste simulador.</p>}
          </div>
          {!campanha && <div className="tool-actions"><button className="secondary-button" type="button" onClick={clear}>Limpar</button></div>}
        </section>
        <section className="tool-card tool-result">
          <h3>Resumo</h3>
          {!Number.isFinite(valor) || valor <= 0 ? <Status kind="warn">Informe um valor válido maior que zero.</Status> : entrada == null ? <Status kind="warn">Escolha a entrada para continuar.</Status> : !validSelected ? <Status kind="error">Nenhuma opção selecionada atende à regra mínima de {formatBRL(minimoParcela)} por parcela e ao limite de prazo.</Status> : <Status kind="ok">{campanha ? 'Simulação da campanha pronta para conferir; esta ação não formaliza um acordo.' : 'Simulação pronta para copiar e usar no atendimento.'}</Status>}
          <div className="tool-metrics two"><Metric label="Valor total" value={Number.isFinite(valor) && valor > 0 ? formatBRL(valor) : '—'} /><Metric label="Entrada" value={validSelected ? `${formatBRL(validSelected.entrada)} (${Math.round(validSelected.entradaPerc * 100)}%)` : entrada != null ? `${Math.round(entrada * 100)}%` : '—'} /><Metric label="Restante" value={validSelected ? formatBRL(validSelected.restante) : '—'} /><Metric label="Parcelamento" value={validSelected ? `${validSelected.parcelas}x de ${formatBRL(validSelected.valorParcela)}` : '—'} /></div>
          <div className="result-copy">{validSelected ? `${campanha ? `FIQUE EM DIA 2026 - ETAPA ${campanha.etapa} | ` : ''}VALOR TOTAL: ${formatBRL(validSelected.valor)} | ENTRADA ${Math.round(validSelected.entradaPerc * 100)}%: ${formatBRL(validSelected.entrada)} | ${validSelected.parcelas}X DE ${formatBRL(validSelected.valorParcela)}` : '—'}</div>
          <button className="primary-button compact" type="button" disabled={!validSelected} onClick={() => void copySummary()}>Copiar resumo</button>
          {copyMessage && <small className="muted">{copyMessage}</small>}
        </section>
      </div>
    </section>
  )
}

function NegociacaoTool() {
  const [valor, setValor] = useState('')
  const [dataDivida, setDataDivida] = useState('')
  const [dataRef, setDataRef] = useState(todayBRString())
  const [result, setResult] = useState<NegociacaoResult | null>(null)

  function submit(event: FormEvent) {
    event.preventDefault()
    setResult(calcularNegociacao(parseBRNumber(valor), parseDateBR(dataDivida), parseDateBR(dataRef)))
  }

  function clear() { setValor(''); setDataDivida(''); setDataRef(todayBRString()); setResult(null) }

  return (
    <section className="operational-tool">
      <ToolHeader title="Sistema de Negociação de Dívidas" description="Faixa de atraso, descontos à vista/parcelado e limite máximo de parcelas em uma resposta pronta." />
      <div className="tool-layout">
        <form className="tool-card tool-form compact" onSubmit={submit}>
          <label>Valor da dívida (R$)<input inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="10.000,50" /></label>
          <label>Data da dívida (dd/mm/aaaa)<input value={dataDivida} onChange={(e) => setDataDivida(e.target.value)} placeholder="01/01/2024" /></label>
          <label>Data para cálculo (dd/mm/aaaa)<input value={dataRef} onChange={(e) => setDataRef(e.target.value)} /></label>
          <div className="tool-rule"><span>Critério</span><strong>Faixa de atraso + desconto por modalidade</strong></div>
          <div className="tool-actions span-2"><button type="button" className="secondary-button" onClick={clear}>Limpar</button><button className="primary-button compact" type="submit">Calcular</button></div>
        </form>
        <section className="tool-card tool-result">
          <h3>Resultado</h3>
          {!result && <Status>Informe os dados e calcule.</Status>}
          {result && 'error' in result && <Status kind="error">{result.error}</Status>}
          {result && !('error' in result) && (
            <>
              <Status kind="ok">Proposta de negociação calculada com sucesso.</Status>
              <div className="tool-metrics three"><Metric label="Dívida original" value={formatBRL(parseBRNumber(valor))} /><Metric label="Dias em atraso" value={formatInt(result.dias)} /><Metric label="Faixa aplicada" value={result.faixaLabel} /></div>
              <div className="proposal-grid"><article><h4>🏷️ À vista</h4><ul><li>Desconto: <strong>{result.descontoAvista}%</strong></li><li>Paga: <strong>{formatBRL(result.valorAvista)}</strong></li><li>Economiza: <strong>{formatBRL(result.economiaAvista)}</strong></li></ul></article><article><h4>💳 Parcelado</h4><ul><li>Desconto: <strong>{result.descontoParcelado}%</strong></li><li>Paga: <strong>{formatBRL(result.valorParcelado)}</strong></li><li>Economiza: <strong>{formatBRL(result.economiaParcelado)}</strong></li><li>Limite: <strong>até {result.maxParcelas}x</strong></li></ul></article></div>
              <div className="result-copy">À vista: pagar {formatBRL(result.valorAvista)} com economia de {formatBRL(result.economiaAvista)} ({result.descontoAvista}% de desconto). Parcelado: pagar {formatBRL(result.valorParcelado)} com economia de {formatBRL(result.economiaParcelado)} ({result.descontoParcelado}% de desconto), em até {result.maxParcelas}x.</div>
            </>
          )}
        </section>
      </div>
    </section>
  )
}

export function OperationalTool({ tool, parcelamentoCampanha, onVoltarCampanha }: {
  tool: ToolKey
  parcelamentoCampanha?: ParcelamentoCampanha | null
  onVoltarCampanha?: () => void
}) {
  if (tool === 'desconto') return <DescontoTool />
  if (tool === 'retro') return <RetroativoTool />
  if (tool === 'tarifa') return <TarifaTool />
  if (tool === 'parcel') return <ParcelamentoTool campanha={parcelamentoCampanha} onVoltarCampanha={onVoltarCampanha} />
  return <NegociacaoTool />
}
